import { ISize2 } from "@base/math/Size2";
import { NormalizedRecord } from "@base/util/NormalizedRecord";
import { SyncClient, SyncServer } from "@base/util/SyncHelper";
import { PropertyNamesWithType } from "@base/util/TypeUtil";
import { SceneType } from "@biz/Definitions";
import { IVariantData3d } from "@biz/definitions/IVariantData3d";
import { ICurtain2dForm } from "@biz/items/curtain/CurtainDefinitions";
import { ILounge2dForm, IProduct2dForm, IRaff2dForm, ISail2dForm, IStandardCollection2dForm } from "@biz/models/Sail2dDefinitions";
import { IShoppingList } from "@biz/models/ShoppingListDefinitions";
import { ISCData } from "@biz/sc/IShopData";

export const VARIANT_VERSION_MIN = 113;
export const VARIANT_VERSION = 116;

export interface ILockedByInfo {
  userId: string;
  companyId: number;
  lockTime: number;
}

export interface IUploadHistory {
  date: number;
  sceneId: string;
  type: SceneType;
}

export interface ICloneHistory {
  date: number;
  sourceId: string;
  ownerId: string;
}

/** @depreacted */
export interface IProjectPreviewOld {
  thumb: string;
  image: string;
}

export interface IProjectPreviewNew {
  thumb: ITokenFile;
  image: ITokenFile;
}

export interface IUserFileInfo {
  id: string;
  type: "texture" | "specialConsole" | "sunSail" | "variant";
  mimeType: string;
  name: string;
  token: string;
  imageSize?: ISize2;
}

export interface IVariantDataCommon {
  shoppingList: IShoppingList; // common shopping cart
  userFiles?: Record<string, IUserFileInfo>;
}

export interface IVariantData2d extends IVariantDataCommon {
  type: "sail2d";
  tabId: string;
  sunSails: NormalizedRecord<string, ISail2dForm>;
  curtains: NormalizedRecord<string, ICurtain2dForm>;
  raffs: NormalizedRecord<string, IRaff2dForm>;
  lounges: NormalizedRecord<string, ILounge2dForm>;
  standardCollections: NormalizedRecord<string, IStandardCollection2dForm>;
}

export interface IVariantDataShop extends IVariantDataCommon {
  type: "shop"
}

export interface IVariantDataSC extends IVariantDataCommon {
  type: "SC";
  product: ISCData;
}

export type VariantDataType = IVariantData2d | IVariantData3d | IVariantDataShop;
export type VariantDataTypes = VariantDataType["type"];

export interface ITokenFile {
  id: string;
  token: string;
}

export interface ITokenFileWithName extends ITokenFile {
  fileName: string;
}

export interface IVariant {
  _id: string;
  variantName: string;
  projectId: string;
  version: number;
  versionConfirmed?: { version: number, timestamp: number };
  owner: string;
  ownerCompanyId?: number;
  token: string;
  preview?: IProjectPreviewOld | IProjectPreviewNew;
  cloneHistory?: ICloneHistory[];
  uploadHistory?: IUploadHistory[];
  lockedBy?: ILockedByInfo;
  data: VariantDataType;
}

type Product2dType = IProduct2dForm["productType"];
export const product2dTypeInfos: Record<Product2dType, { data: Product2dRecord }> = {
  sunSail2d: { data: "sunSails" },
  raff2d: { data: "raffs" },
  curtain2d: { data: "curtains" },
  lounge2d: { data: "lounges" },
  standardCollection2d: { data: "standardCollections" },
}
export type Product2dRecord = PropertyNamesWithType<IVariantData2d, NormalizedRecord<PropertyKey, unknown>>;


export type ILocalVariant = SyncClient<IVariant>;
export type IServerVariant = SyncServer<IVariant>;
export interface IVariantSummary extends Pick<IVariant, "_id" | "token" | "projectId" | "variantName" | "uploadHistory" | "lockedBy"> {
  preview?: IProjectPreviewNew;
  type: IVariant["data"]["type"];
  userFileIds: string[];
}
export type IServerVariantSummary = SyncServer<IVariantSummary>;